import {
  hasAccess,
  type AccessContext,
  type AccessRequirement,
} from '../auth/permissions';

/** A sidebar item plus the access it requires (undefined = always visible). */
export interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly requirement?: AccessRequirement;
}

/**
 * Central nav config. Each item declares its access requirement using the same
 * shape as the route guard, so a surface is gated and hidden-from-menu in one
 * place. Add real Phase 1+ surfaces here with their `capability` / `region`
 * requirements as they land.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/stores',
    label: 'Stores',
    requirement: { capability: 'stores:read' },
  },
  {
    to: '/users',
    label: 'Users',
    requirement: { capability: 'users:read' },
  },
  {
    to: '/cashbacks',
    label: 'Cashbacks',
    requirement: { capability: 'cashbacks:read' },
  },
  {
    to: '/cashouts',
    label: 'Cashouts',
    requirement: { capability: 'cashouts:read' },
  },
  {
    to: '/charities',
    label: 'Charities',
    requirement: { capability: 'charities:read' },
  },
  {
    to: '/categories',
    label: 'Categories',
    requirement: { capability: 'categories:read' },
  },
  {
    to: '/affiliateNetworks',
    label: 'Networks',
    requirement: { capability: 'networks:read' },
  },
  {
    // Store tags (Phase 8, D-multi-channel-editor). The tag-vocabulary management
    // surface (create + edit, immutable key, no delete). Gated stores:write so the
    // editor and super_admin see the MANAGEMENT entry; a viewer's read-everywhere
    // '*:read' does not surface it (assignment happens in the store editor, the
    // vocabulary itself is reference data).
    to: '/storeTags',
    label: 'Store tags',
    requirement: { capability: 'stores:write' },
  },
  {
    // Offline stores (Phase 8, D-offline-stores). Brick-and-mortar / card-linked-offer
    // venues, its own collection. Gated stores:read to view (the stores family); a
    // viewer's '*:read' sees it like the online stores list, the management actions
    // gate stores:write.
    to: '/offlineStores',
    label: 'Offline stores',
    requirement: { capability: 'stores:read' },
  },
  {
    // Rate rules (Phase 8, surface 2, D-rate-rules). The rule-authoring tool with the
    // honest projection previews. Gated by ROLE to editor and super_admin (not a
    // capability), so a viewer's read-everywhere '*:read' cannot reach it.
    to: '/rateRules',
    label: 'Rate rules',
    requirement: { roles: ['editor', 'super_admin'] },
  },
  {
    // Rate rule calendar (Phase 8, surface 2, D-rate-rules). The scheduling lens over
    // the derived lifecycle states. Same ROLE gate as the rules list.
    to: '/rate-rule-calendar',
    label: 'Rule calendar',
    requirement: { roles: ['editor', 'super_admin'] },
  },
  {
    // Page schemas (Phase 8, surface 3a, D-page-schema-editor-8a). The authoring core
    // of the schema-driven block editor (drafts only). Gated by ROLE to editor and
    // super_admin (not a capability), so a viewer's read-everywhere '*:read' cannot
    // reach it.
    to: '/pageSchemas',
    label: 'Page schemas',
    requirement: { roles: ['editor', 'super_admin'] },
  },
  {
    // CMS pages (Phase 8, conventional rich-text CMS, sub-surface (a), D-cms-pages-core).
    // The marketing-document authoring surface (TipTap rich text), DISTINCT from the
    // page-schema app screens. Gated by ROLE to editor and super_admin (not a capability),
    // so a viewer's read-everywhere '*:read' cannot reach it.
    to: '/cmsPages',
    label: 'CMS pages',
    requirement: { roles: ['editor', 'super_admin'] },
  },
  {
    // Blog posts (Phase 8, conventional rich-text CMS, sub-surface (b), D-cms-blog).
    // REUSES the cmsPages rich-text body and lifecycle in a SEPARATE collection plus the
    // blog fields. Gated by ROLE to editor and super_admin (not a capability), so a
    // viewer's read-everywhere '*:read' cannot reach it.
    to: '/blogPosts',
    label: 'Blog posts',
    requirement: { roles: ['editor', 'super_admin'] },
  },
  {
    // Blog categories (Phase 8, D-cms-blog). The managed editorial-category vocabulary
    // cloned from storeTags (immutable key, no delete). Reference data, role-gated to
    // editor and super_admin so a viewer's '*:read' does not surface it.
    to: '/blogCategories',
    label: 'Blog categories',
    requirement: { roles: ['editor', 'super_admin'] },
  },
  {
    // Testimonials (Phase 8, conventional rich-text CMS, sub-surface (c1),
    // D-cms-testimonials-seo). A FLAT structured surface (no rich text), the active /
    // inactive toggle. Gated by ROLE to editor and super_admin (not a capability), so a
    // viewer's read-everywhere '*:read' cannot reach it.
    to: '/testimonials',
    label: 'Testimonials',
    requirement: { roles: ['editor', 'super_admin'] },
  },
  {
    // SEO rules (Phase 8, D-cms-testimonials-seo). Per-page meta keyed by a FREE pageKey /
    // path (no FK), with per-market region variants. A FLAT structured surface, role-gated
    // to editor and super_admin so a viewer's '*:read' does not surface it.
    to: '/seoRules',
    label: 'SEO rules',
    requirement: { roles: ['editor', 'super_admin'] },
  },
  {
    // Sliders (Phase 8, conventional rich-text CMS, sub-surface (c2), D-cms-sliders).
    // Marketing-site slides grouped by placement, the dnd-kit per-placement reorder. Gated
    // by ROLE to editor and super_admin (not a capability), so a viewer's read-everywhere
    // '*:read' cannot reach it.
    to: '/sliderSlides',
    label: 'Sliders',
    requirement: { roles: ['editor', 'super_admin'] },
  },
  {
    // Navigation menu (Phase 8, conventional rich-text CMS, sub-surface (d), D-cms-menu,
    // the LAST CMS piece). A nested link tree grouped by location, the dnd-kit per-level
    // reorder. Gated by ROLE to editor and super_admin (not a capability), so a viewer's
    // read-everywhere '*:read' cannot reach it.
    to: '/menuItems',
    label: 'Navigation menu',
    requirement: { roles: ['editor', 'super_admin'] },
  },
  {
    // Reports > Earnings (Phase 3). The first analytical surface. Gated by the
    // read capability `earnings:read`: viewer (read everywhere) and super_admin
    // see it; the descriptive editor and placeholder support / fraud do not -
    // margin is sensitive financial data (D-earnings).
    to: '/earnings',
    label: 'Earnings',
    requirement: { capability: 'earnings:read' },
  },
  {
    // Reports > Store performance (Phase 3). The suite's first funnel surface.
    // Same read-capability trust line as Earnings: viewer and super_admin see it,
    // the descriptive editor and placeholder support / fraud do not.
    to: '/store-performance',
    label: 'Store performance',
    requirement: { capability: 'storePerformance:read' },
  },
  {
    // Reports > Exit clicks (Phase 3). The destination-side mirror of Store
    // performance and the final reports-suite funnel surface. Same read-capability
    // trust line: viewer and super_admin see it, the descriptive editor and
    // placeholder support / fraud do not (D-exit-clicks).
    to: '/exit-clicks',
    label: 'Exit clicks',
    requirement: { capability: 'exitClicks:read' },
  },
  {
    // Members > Plus subscriptions (Phase 4). A filtered view over users that
    // MUTATES (cancel renewal, comp Plus). Read for viewer / editor / super_admin
    // via subscriptions:read; the two write actions gate on finer capabilities
    // (D-subscriptions).
    to: '/subscriptions',
    label: 'Plus subscriptions',
    requirement: { capability: 'subscriptions:read' },
  },
  {
    // Promo codes (Phase 4). Managed CRUD, Charities-shaped. Read for viewer /
    // editor / super_admin via promo_codes:read; create gates on promo_codes:issue
    // and edit on promo_codes:write (D-promo-codes).
    to: '/promoCodes',
    label: 'Promo codes',
    requirement: { capability: 'promo_codes:read' },
  },
  {
    // Referrals (Phase 4). A read-mostly explorer over the user referral graph,
    // with one guarded action (void). Read for viewer / editor / super_admin via
    // referrals:read; void gates on referrals:void (D-referrals).
    to: '/referrals',
    label: 'Referrals',
    requirement: { capability: 'referrals:read' },
  },
  {
    // Placements (Phase 4). Managed CRUD, Charities-shaped, internal merchandising.
    // Read for viewer / editor / super_admin via placements:read; create and edit
    // both gate on placements:write (single trust tier, no value-split; placement
    // is ended via its window, not deactivated; D-placements).
    to: '/placements',
    label: 'Placements',
    requirement: { capability: 'placements:read' },
  },
  {
    // Manual attribution (standalone ops surface, ahead of Phase 5). A review queue
    // over orphan transactions. Read for viewer / editor / super_admin via
    // attribution:read; credit gates on attribution:credit (super_admin), reject and
    // flag on attribution:reject / attribution:flag (editor) (D-manual-attribution).
    to: '/manual-attribution',
    label: 'Manual attribution',
    requirement: { capability: 'attribution:read' },
  },
  {
    // Fraud queue (Phase 5 lead). A sensitive ranked view over flagged users with
    // recorded-decision actions. Gated by ROLE, not capability (like audit): a
    // viewer's read-everywhere '*:read' must not reach it, so only the fraud
    // reviewer and super_admin see it (D-fraud-queue).
    to: '/fraud-queue',
    label: 'Fraud queue',
    requirement: { roles: ['fraud', 'super_admin'] },
  },
  {
    // Suspensions (Phase 5, surface 2). The suspension-lifecycle worklist over held
    // users. Gated by ROLE like the fraud queue (not a capability), so a viewer's
    // read-everywhere '*:read' cannot reach it: only fraud and super_admin see it
    // (D-suspension-flow).
    to: '/suspensions',
    label: 'Suspensions',
    requirement: { roles: ['fraud', 'super_admin'] },
  },
  {
    // Ban evasion (Phase 5, surface 3). A detection surface over suspected
    // same-person accounts evading a ban. Gated by ROLE like the fraud queue and
    // suspensions (not a capability), so a viewer's read-everywhere '*:read' cannot
    // reach it: only fraud and super_admin see it (D-ban-evasion).
    to: '/ban-evasion',
    label: 'Ban evasion',
    requirement: { roles: ['fraud', 'super_admin'] },
  },
  {
    // Chargebacks (Phase 5, surface 4). The disputed-transaction lifecycle. Gated by
    // ROLE like the rest of the fraud phase (not a capability), so a viewer's
    // read-everywhere '*:read' cannot reach the sensitive financial work: only fraud
    // and super_admin see it (D-chargebacks).
    to: '/chargebacks',
    label: 'Chargebacks',
    requirement: { roles: ['fraud', 'super_admin'] },
  },
  {
    // Balance adjustments (Phase 5, surface 5, the capstone). The oversight ledger of
    // manual wallet credits and debits. Gated by ROLE to super_admin ONLY (not fraud):
    // a manual adjustment moves value directly and is the highest-trust write, so even
    // a viewer's read-everywhere '*:read' must not reach it (D-balance-adjustment).
    to: '/adjustments',
    label: 'Balance adjustments',
    requirement: { roles: ['super_admin'] },
  },
  {
    // Ticket inbox (Phase 6 LEAD). The support agent's worklist over member tickets.
    // Gated by ROLE to support and super_admin (not a capability), so a viewer's
    // read-everywhere '*:read' cannot reach it and the editor / fraud reviewer do not
    // see it (D-ticket-inbox).
    to: '/tickets',
    label: 'Ticket inbox',
    requirement: { roles: ['support', 'super_admin'] },
  },
  {
    // Canned responses (Phase 6, surface 2). The shared template + macro library. Gated by
    // canned_responses:write so only the manager (super_admin via '*') sees the MANAGEMENT
    // surface in the menu: the support agent USES templates from the ticket inbox, not from
    // here, so it needs no menu entry of its own (D-canned-responses).
    to: '/cannedResponses',
    label: 'Canned responses',
    requirement: { capability: 'canned_responses:write' },
  },
  {
    // Data subject requests (Phase 7 LEAD). The compliance officer's worklist over member
    // DSARs. Gated by ROLE to compliance and super_admin (not a capability), so a viewer's
    // read-everywhere '*:read' cannot reach it and the operational roles do not see it
    // (D-dsar).
    to: '/dsar',
    label: 'Data subject requests',
    requirement: { roles: ['compliance', 'super_admin'] },
  },
  {
    // Sanctions screening (Phase 7, surface 3). The compliance officer's review queue over the
    // engine-written sanctions matches. Gated by ROLE to compliance and super_admin (not a
    // capability), so a viewer's read-everywhere '*:read' cannot reach it and the operational
    // roles do not see it (D-sanctions).
    to: '/sanctions',
    label: 'Sanctions screening',
    requirement: { roles: ['compliance', 'super_admin'] },
  },
  {
    // Tax reporting / 1099-K (Phase 7, surface 4). A pure derived read of US members over the IRS
    // reporting threshold. Gated by ROLE to compliance and super_admin (not a capability), so a
    // viewer's read-everywhere '*:read' cannot reach it and the operational roles do not see it
    // (D-tax-reporting).
    to: '/tax',
    label: 'Tax reporting',
    requirement: { roles: ['compliance', 'super_admin'] },
  },
  {
    // Erasure / right-to-be-forgotten (Phase 7, surface 6, the last and sharpest). The compliance
    // worklist over erasure requests. Gated by ROLE to compliance and super_admin (not a capability),
    // so a viewer's read-everywhere '*:read' cannot reach it; the irreversible execute is additionally
    // capability-gated to super_admin within the surface (the maker-checker, D-erasure).
    to: '/erasure',
    label: 'Erasure requests',
    requirement: { roles: ['compliance', 'super_admin'] },
  },
  {
    // Settings (Phase 9, S1, D-settings-s1). The singleton-config foundation: system
    // config + feature flags + the Plus marketing flag. Gated by settings:write so the
    // editor and super_admin see it; a viewer's read-everywhere '*:read' does not surface
    // it (there is nothing to read-only-view here, it is a config surface). The
    // super_admin-only marketing flag control gates within the page.
    to: '/settings',
    label: 'Settings',
    requirement: { capability: 'settings:write' },
  },
  {
    // API docs (Phase 9, S1, D-settings-s1). A static internal reference of the admin API
    // route groups. Gated settings:read so any admin with a read capability sees it: the
    // editor (granted settings:read), super_admin (via '*') and a viewer (via '*:read').
    to: '/api-docs',
    label: 'API docs',
    requirement: { capability: 'settings:read' },
  },
  {
    // Email templates (Phase 9, Settings S3, D-settings-s3-email-templates). The fixed
    // transactional set, edit-only. Gated email_templates:write so the editor and
    // super_admin see it; a viewer's read-everywhere '*:read' does not surface it (it
    // is a config surface, not a read-only view).
    to: '/emailTemplates',
    label: 'Email templates',
    requirement: { capability: 'email_templates:write' },
  },
  {
    // US compliance settings (Phase 9, Settings S4, D-settings-s4-us-compliance), the last
    // Settings surface. The 1099-K thresholds, per-state privacy toggles and privacy / terms
    // references. Gated by ROLE to compliance and super_admin (the tax-report / erasure
    // precedent): a viewer's read-everywhere '*:read' cannot reach it and editor is denied.
    // Compliance owns regulatory config, the tightest hand in Settings.
    to: '/compliance-settings',
    label: 'US compliance',
    requirement: { roles: ['compliance', 'super_admin'] },
  },
  {
    // Events explorer (Phase 9, D-events-explorer). A read-only debug / search tool over the
    // events stream for support and engineering. Gated by ROLE to support, super_admin and
    // fraud (the debug audience): the surface exposes member behavioural traces, so the role
    // gate holds the boundary against a viewer's read-everywhere '*:read' (the AuditList
    // precedent, a bare events:read capability would leak to viewer).
    to: '/events',
    label: 'Events explorer',
    requirement: { roles: ['support', 'super_admin', 'fraud'] },
  },
  {
    // Internal alerts config (Phase 9, D-internal-alerts). The CONFIG half: thresholds,
    // channels, on-off. Gated by CAPABILITY alerts:write, which only super_admin holds (via
    // '*'): consequential operational platform config sits at the tightest tier.
    to: '/alert-config',
    label: 'Alert config',
    requirement: { capability: 'alerts:write' },
  },
  {
    // Fired-alert trail (Phase 9, D-internal-alerts). The read-only history half. Gated by
    // ROLE to super_admin, fraud and support (the operational responders who act on alerts):
    // a broader read than the config write, held against a viewer's '*:read' (the split).
    to: '/firedAlerts',
    label: 'Fired alerts',
    requirement: { roles: ['super_admin', 'fraud', 'support'] },
  },
  {
    // Audit is super_admin AND compliance oversight. Gated by ROLE, not capability: the
    // viewer's read-everywhere `*:read` wildcard would satisfy a capability gate, so the
    // explicit role gate is what keeps the trail to super_admin and compliance. Compliance
    // joins as the independent oversight reader, the considered gate expansion held back from
    // the DSAR build (D-audit-compliance-read).
    to: '/audit',
    label: 'Audit',
    requirement: { roles: ['super_admin', 'compliance'] },
  },
];

/** The nav items the given access context is allowed to see. */
export function visibleNavItems(
  ctx: AccessContext,
  items: readonly NavItem[] = NAV_ITEMS,
): NavItem[] {
  return items.filter(
    (item) => !item.requirement || hasAccess(ctx, item.requirement),
  );
}
